import { fetchAPI, processStrapiData } from './client';

/**
 * Obtiene el locale actual de la aplicación
 * @returns Código de idioma actual (ej: 'es', 'en')
 */
function getCurrentLocale(): string {
  // En el cliente, obtener del localStorage
  if (typeof window !== 'undefined') {
    return localStorage.getItem('tolgee-language') || 'es';
  }
  // En el servidor, usar el valor por defecto
  return 'es';
}

// Interfaces para tipado
export interface StrapiImage {
  id: number;
  url: string;
  width: number;
  height: number;
  alternativeText?: string | null;
  caption?: string | null;
  formats?: {
    thumbnail?: { url: string; width: number; height: number };
    small?: { url: string; width: number; height: number };
    medium?: { url: string; width: number; height: number };
    large?: { url: string; width: number; height: number };
  };
}

export interface StrapiCarouselSlide {
  id: number;
  Title?: string;  // Nota la 'T' mayúscula según la respuesta de la API
  title?: string;  // Mantener para compatibilidad
  subtitle?: string;
  buttonText?: string;
  buttonLink?: string;
  image?: StrapiImage;  // La imagen puede estar directamente en el objeto
  order?: number;
}

export interface StrapiHomeBentoItem {
  id: number;
  image?: StrapiImage;
  link: string;
  order?: number;
  openInNewTab?: boolean;
}

export interface StrapiBlogPost {
  id: number;
  title?: string;
  Title?: string;
  slug?: string;
  content?: string;
  excerpt?: string;
  publishedAt?: string;
  image?: StrapiImage;
  category?: {
    id: number;
    name?: string;
    slug?: string;
  };
}

export interface StrapiCategory {
  id: number;
  name: string;
  slug: string;
  description?: string;
  image?: StrapiImage;
}

export interface StrapiProduct {
  id: number;
  title: string;
  description?: string;
  price?: number;
  slug: string;
  image?: StrapiImage;
  gallery?: StrapiImage[];
  category?: StrapiCategory;
}

/**
 * Registra un error en la consola y devuelve un valor predeterminado
 * @param error - El error capturado
 * @param message - Mensaje de error personalizado
 * @param defaultValue - Valor predeterminado a devolver
 * @returns Valor predeterminado
 */
function handleApiError<T>(error: any, message: string, defaultValue: T): T {
  console.error(message, error);
  return defaultValue;
}

/**
 * Procesa la respuesta de Strapi para adaptarla a nuestro formato
 * @param data - Datos de respuesta de Strapi
 * @returns Datos procesados en nuestro formato
 */
function processSlideData(slides: any[]): StrapiCarouselSlide[] {
  return slides.map(slide => {
    // Si viene en el formato estándar de Strapi con 'attributes'
    const attrs = slide.attributes || slide;
    
    // Obtener el título (puede estar como 'title' o 'Title')
    const title = attrs.title || attrs.Title || '';
    
    // Extraer la imagen (puede estar en diferentes formatos)
    const image = attrs.image;
    
    // Convertir el objeto para que use nuestra estructura
    return {
      id: slide.id,
      title: title,
      Title: title, // Guardar en ambos campos para compatibilidad
      subtitle: attrs.subtitle,
      buttonText: attrs.buttonText,
      buttonLink: attrs.buttonLink,
      image: image,
      order: attrs.order || 0
    };
  });
}

/**
 * Obtiene las diapositivas del carrusel
 * @returns Array de diapositivas del carrusel
 */
export async function getCarouselSlides(): Promise<StrapiCarouselSlide[]> {
  try {
    // Obtener el locale actual automáticamente
    const currentLocale = getCurrentLocale();
    
    // Preparar parámetros de consulta
    const queryParams: Record<string, any> = {
      'pagination[limit]': 10,
      'populate': 'image',
      'locale': currentLocale,
    };
    
    // Intentar primero con "carousel-slides" (confirmado como correcto)
    try {
      const data = await fetchAPI('/carousel-slides', queryParams);
      
      if (data && data.data && data.data.length > 0) {
        return processSlideData(data.data);
      }
    } catch (error) {
      console.log('No se encontró el endpoint /carousel-slides, probando alternativas...');
    }
    
    // Si no funciona, intentar con otros endpoints como respaldo
    try {
      const data = await fetchAPI('/slides', queryParams);
      
      if (data && data.data && data.data.length > 0) {
        return processSlideData(data.data);
      }
    } catch (error) {
      console.log('No se encontró el endpoint /slides, probando alternativas...');
    }
    
    // Si no existe, probar con "carousels"
    try {
      const data = await fetchAPI('/carousels', queryParams);
      
      if (data && data.data && data.data.length > 0) {
        return processSlideData(data.data);
      }
    } catch (error) {
      console.log('No se encontró el endpoint /carousels');
    }
    
    // Si llegamos aquí, no se encontró ningún endpoint válido
    console.log('No se encontró ningún endpoint válido para el carrusel');
    return [];
  } catch (error) {
    return handleApiError(error, 'Error fetching carousel slides:', []);
  }
}

/**
 * Obtiene las imágenes enlazadas del bento horizontal de la home.
 */
export async function getHomeBentoItems(): Promise<StrapiHomeBentoItem[]> {
  try {
    const data = await fetchAPI('/home-bentos', {
      'pagination[limit]': 24,
      'populate': 'image',
      'sort[0]': 'order:asc',
      'locale': getCurrentLocale(),
    });

    if (!data?.data || !Array.isArray(data.data)) {
      return [];
    }

    return data.data
      .map((item: any) => {
        const attrs = item.attributes || item;

        return {
          id: item.id,
          image: attrs.image,
          link: attrs.link || '',
          order: attrs.order || 0,
          openInNewTab: attrs.openInNewTab || false,
        };
      })
      .filter((item: StrapiHomeBentoItem) => item.image && item.link);
  } catch (error) {
    return handleApiError(error, 'Error fetching home bento items:', []);
  }
}

/**
 * Obtiene los posts del blog
 * @param limit - Número de posts a obtener (por defecto 10)
 * @param start - Índice de inicio para paginación
 * @returns Array de posts del blog
 */
export async function getBlogPosts(
  limit: number = 10,
  start: number = 0
): Promise<StrapiBlogPost[]> {
  try {
    // Intentar primero con el endpoint plural "blog-posts"
    try {
      const data = await fetchAPI('/blog-posts', {
        'pagination[limit]': limit,
        'pagination[start]': start,
        'populate': 'image',
      });
      
      if (data && data.data && data.data.length > 0) {
        return processStrapiData(data.data);
      }
    } catch (error) {
      console.log('No se encontró el endpoint /blog-posts, probando alternativas...');
    }
    
    // Si no existe, probar con "articles"
    try {
      const data = await fetchAPI('/articles', {
        'pagination[limit]': limit,
        'pagination[start]': start,
        'populate': 'image',
      });
      
      if (data && data.data && data.data.length > 0) {
        return processStrapiData(data.data);
      }
    } catch (error) {
      console.log('No se encontró el endpoint /articles, probando alternativas...');
    }
    
    // Si no existe, probar con "posts"
    try {
      const data = await fetchAPI('/posts', {
        'pagination[limit]': limit,
        'pagination[start]': start,
        'populate': 'image',
      });
      
      if (data && data.data && data.data.length > 0) {
        return processStrapiData(data.data);
      }
    } catch (error) {
      console.log('No se encontró el endpoint /posts');
    }
    
    // Si llegamos aquí, no se encontró ningún endpoint válido
    console.log('No se encontró ningún endpoint válido para el blog');
    return [];
  } catch (error) {
    return handleApiError(error, 'Error fetching blog posts:', []);
  }
}

/**
 * Obtiene un post del blog por su slug
 * @param slug - Slug del post
 * @returns Post del blog o null si no se encuentra
 */
export async function getBlogPostBySlug(slug: string): Promise<StrapiBlogPost | null> {
  try {
    // Intentar primero con el endpoint plural "blog-posts"
    try {
      const data = await fetchAPI('/blog-posts', {
        'filters[slug][$eq]': slug,
        'populate': 'image',
        'pagination[limit]': 1,
      });
      
      if (data && data.data && data.data.length > 0) {
        return processStrapiData(data.data[0]);
      }
    } catch (error) {
      console.log('No se encontró el endpoint /blog-posts para slug, probando alternativas...');
    }
    
    // Si no existe, probar con "articles"
    try {
      const data = await fetchAPI('/articles', {
        'filters[slug][$eq]': slug,
        'populate': 'image',
        'pagination[limit]': 1,
      });
      
      if (data && data.data && data.data.length > 0) {
        return processStrapiData(data.data[0]);
      }
    } catch (error) {
      console.log('No se encontró el endpoint /articles para slug, probando alternativas...');
    }
    
    // Si no existe, probar con "posts"
    try {
      const data = await fetchAPI('/posts', {
        'filters[slug][$eq]': slug,
        'populate': 'image',
        'pagination[limit]': 1,
      });
      
      if (data && data.data && data.data.length > 0) {
        return processStrapiData(data.data[0]);
      }
    } catch (error) {
      console.log('No se encontró el endpoint /posts para slug');
    }
    
    // Si llegamos aquí, no se encontró ningún endpoint válido
    console.log('No se encontró ningún endpoint válido para el post específico');
    return null;
  } catch (error) {
    return handleApiError(error, 'Error fetching blog post by slug:', null);
  }
}

/**
 * Obtiene las categorías de productos
 * @returns Array de categorías
 */
export async function getCategories(): Promise<StrapiCategory[]> {
  try {
    // Intentar con "categories"
    try {
      const data = await fetchAPI('/categories', {
        'pagination[limit]': 100,
      });
      
      if (data && data.data && data.data.length > 0) {
        return processStrapiData(data.data);
      }
    } catch (error) {
      console.log('No se encontró el endpoint /categories, probando alternativas...');
    }
    
    // Si no existe, probar con "product-categories"
    try {
      const data = await fetchAPI('/product-categories', {
        'pagination[limit]': 100,
      });
      
      if (data && data.data && data.data.length > 0) {
        return processStrapiData(data.data);
      }
    } catch (error) {
      console.log('No se encontró el endpoint /product-categories');
    }
    
    // Si llegamos aquí, no se encontró ningún endpoint válido
    console.log('No se encontró ningún endpoint válido para categorías');
    return [];
  } catch (error) {
    return handleApiError(error, 'Error fetching categories:', []);
  }
}

/**
 * Obtiene los productos
 * @param categorySlug - Slug de la categoría (opcional)
 * @param limit - Número de productos a obtener (por defecto 50)
 * @param start - Índice de inicio para paginación
 * @returns Array de productos
 */
export async function getProducts(
  categorySlug?: string,
  limit: number = 50,
  start: number = 0
): Promise<StrapiProduct[]> {
  try {
    // Preparar filtros
    const filters: Record<string, any> = {};
    if (categorySlug) {
      filters['filters[category][slug][$eq]'] = categorySlug;
    }
    
    // Intentar con "products"
    try {
      const data = await fetchAPI('/products', {
        ...filters,
        'pagination[limit]': limit,
        'pagination[start]': start,
      });
      
      if (data && data.data && data.data.length > 0) {
        return processStrapiData(data.data);
      }
    } catch (error) {
      console.log('No se encontró el endpoint /products');
    }
    
    // Si llegamos aquí, no se encontró ningún endpoint válido
    console.log('No se encontró ningún endpoint válido para productos');
    return [];
  } catch (error) {
    return handleApiError(error, 'Error fetching products:', []);
  }
}

/**
 * Obtiene un producto por su slug
 * @param slug - Slug del producto
 * @returns Producto o null si no se encuentra
 */
export async function getProductBySlug(slug: string): Promise<StrapiProduct | null> {
  try {
    // Intentar con "products"
    try {
      const data = await fetchAPI('/products', {
        'filters[slug][$eq]': slug,
        'pagination[limit]': 1,
      });
      
      if (data && data.data && data.data.length > 0) {
        return processStrapiData(data.data[0]);
      }
    } catch (error) {
      console.log('No se encontró el endpoint /products para slug');
    }
    
    // Si llegamos aquí, no se encontró el producto
    return null;
  } catch (error) {
    return handleApiError(error, 'Error fetching product by slug:', null);
  }
} 
